import { getMultipackContentProduct } from '@/lib/multipack';
import { PdpWishlistButton } from '@/components/buttons/pdp-wishlist-button';
import { type ReactElement } from 'react';
import { Badge } from '../ui/badge';
import type { SCAPIShopperProductsSchemas } from 'src/types';

type ProductInfoBaseProps = {
    product: SCAPIShopperProductsSchemas['Product'];
    hideVariantSelection?: boolean;
    /** Layout style: 'full' (default) shows title, description, inventory; 'compact' shows brand, smaller title, sorted attributes */
    variantStyle?: 'full' | 'compact';
    /** When true and mode is 'edit', show quantity picker (e.g. in cart edit modal) */
    showQuantityInEditMode?: boolean;
};
type ProductInfoUncontrolledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'uncontrolled' uses URL navigation */
    swatchMode?: 'uncontrolled';
    onAttributeChange?: never;
    variationValues?: never;
};
type ProductInfoControlledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'controlled' uses callback */
    swatchMode: 'controlled';
    /** Callback when variant attribute changes in controlled mode */
    onAttributeChange: (attributeId: string, value: string) => void;
    /** Controlled variation values for controlled mode (e.g., {color: 'red', size: 'M'}) */
    variationValues: { [key: string]: string };
};
type ProductInfoProps = ProductInfoUncontrolledProps | ProductInfoControlledProps;
/**
 * ProductInfo component displays product details including title, description, price, variants, and quantity picker
 *
 * Supports two swatch modes:
 * - uncontrolled mode (default): Swatches use URL navigation for variant selection
 * - controlled mode: Swatches use callbacks for controlled variant selection (used in modals)
 *
 * @param props - Component props
 * @param props.product - The product data to display
 * @param props.swatchMode - Swatch interaction mode ('uncontrolled' or 'controlled')
 * @param props.onAttributeChange - Callback for controlled mode variant changes
 * @param props.variationValues - Controlled variation values for controlled mode
 * @returns JSX element with product information display
 */
export default function ProductInfoTitle({ product }: ProductInfoProps): ReactElement {
    // const { t } = useTranslation('product');
    const eatingStyle = product.c_pimEatingStyles;
    const productName = product.name;
    const brandName = getMultipackContentProduct(product).c_pimBrandName;
    return (
        <div className="flex flex-col gap-1 w-full relative">
            {brandName && (
                <div className="flex items-center justify-between gap-2">
                    <p className="label-m text-neutral-40">{brandName}</p>
                    <div className="max-lg:hidden">
                        <PdpWishlistButton product={product} size="sm" />
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between gap-2">
                <h1 data-testid="product-title" className="body-copy-l-bold">
                    {productName}
                </h1>
                {!brandName && (
                    <div className="max-lg:hidden">
                        <PdpWishlistButton product={product} size="sm" />
                    </div>
                )}
            </div>
            {eatingStyle && (
                <div className="flex gap-2 flex-wrap mt-3">
                    {eatingStyle.map((style) => (
                        <Badge
                            key={style}
                            className="bg-status-green-40 text-neutral-white truncate max-w-full inline-block">
                            {style}
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    );
}
